import { useEffect, useEffectEvent, useState } from 'react';

interface AutoSelectSoleOptionParams<T> {
    /** The options currently rendered in the selector. */
    options: T[];
    /** Stable identity of an option — the value that would be stored on selection. */
    getKey: (option: T) => string;
    /** True when the selector already holds a value. */
    hasSelection: boolean;
    /** Called once, after render, with the sole option. Must be the same path a click takes. */
    onSelect: (option: T) => void;
    /** Set to false to suppress auto-selection (e.g. disabled / locked selectors). */
    isEnabled?: boolean;
}

/**
 * Selects the only available option of a selector — on first render as well as whenever the
 * option set narrows down to exactly one.
 *
 * The selection is handed over a tick after the effect rather than from inside it: writing to a
 * parent or store within the same commit lets a value land before the UI it depends on has settled
 * (see the ET-4982 step-calendar regression). It is cancelled on cleanup, so a decision the next
 * render no longer stands behind — or a selector that has gone away — cannot still write.
 *
 * Keyed on the sole option's identity, so re-selecting a value the user just cleared never happens,
 * while a *different* sole option still gets selected.
 */
export const useAutoSelectSoleOption = <T>({
    options,
    getKey,
    hasSelection,
    onSelect,
    isEnabled = true,
}: AutoSelectSoleOptionParams<T>): void => {
    const [autoSelectedKey, setAutoSelectedKey] = useState<string | undefined>(undefined);

    // Forgetting an option that left the set is what separates "the user cleared this" (option still
    // on offer, stays cleared) from "the options changed and came back" (selected again).
    if (autoSelectedKey !== undefined && !options.some((option) => getKey(option) === autoSelectedKey)) {
        setAutoSelectedKey(undefined);
    }

    // Length checks rather than truthiness throughout, so a falsy option value still counts as one.
    const soleKey = options.length === 1 ? getKey(options[0]) : undefined;
    const shouldSelect = isEnabled && soleKey !== undefined && !hasSelection && soleKey !== autoSelectedKey;

    // Reads the option set as it stands when the tick elapses, not as it stood when it was scheduled.
    const selectSoleOption = useEffectEvent((): void => {
        if (options.length !== 1 || soleKey === undefined) return;

        setAutoSelectedKey(soleKey);
        onSelect(options[0]);
    });

    useEffect((): (() => void) | undefined => {
        if (!shouldSelect) return undefined;

        const handler = setTimeout((): void => selectSoleOption(), 0);
        return (): void => clearTimeout(handler);
    }, [shouldSelect]);
};
